import { useState, useEffect, useCallback } from 'react';
import { checkScrolledToBottom } from '@/entities/policy';

export function useScrollDetection() {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const scrollRef = useCallback((element: HTMLDivElement | null) => {
    setScrollElement(element);
  }, []);

  /**
   * Measures the element and either auto-enables (content fits) or
   * attaches a scroll listener.  Always runs after a rAF so the browser
   * has had a chance to finish layout before we read scrollHeight.
   */
  const evaluate = useCallback((element: HTMLDivElement) => {
    return requestAnimationFrame(() => {
      // Content fits without any scrollbar → already at the bottom
      if (element.scrollHeight <= element.clientHeight + 2) {
        setHasScrolledToBottom(true);
        return;
      }

      // Content is already scrolled to the bottom (e.g. very short overflow)
      if (checkScrolledToBottom(element)) {
        setHasScrolledToBottom(true);
      }
    });
  }, []);

  useEffect(() => {
    if (!scrollElement) return;

    // Scroll listener — fires while the user scrolls
    const handleScroll = () => {
      if (checkScrolledToBottom(scrollElement)) {
        setHasScrolledToBottom(true);
      }
    };
    scrollElement.addEventListener('scroll', handleScroll);

    // ResizeObserver — fires when HTML content is injected after mount
    const observer = new ResizeObserver(() => {
      setHasScrolledToBottom((prev) => {
        if (prev) return prev; // already done, skip
        evaluate(scrollElement);
        return prev;
      });
    });
    observer.observe(scrollElement);

    // Also evaluate immediately after mount (content may already be there)
    const animationFrame = evaluate(scrollElement);

    return () => {
      cancelAnimationFrame(animationFrame);
      scrollElement.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, [evaluate, scrollElement]);

  const reset = useCallback(() => setHasScrolledToBottom(false), []);

  return { hasScrolledToBottom, scrollRef, reset };
}
